import { Fragment, useEffect, useRef, useState } from "react";
import { parseHorseFormattedName } from "@/utils/parse-horse-name";
import { tipBadge } from "@/utils/performance/constants";

/**
 * The wide, horizontally-scrolling race-by-race detail grid.
 * Sticky Time column; paid positions tinted; Lexi tips badged (E/RD/LT) and
 * coloured hit=emerald / miss=grey; cashed legs get an emerald left-border.
 * Columns show 1st–6th · … · Last (mockup); the "…" cell is clickable to expand
 * the hidden middle placings (7th onward) in a panel beneath the row.
 */

const POSITION_COLS = [1, 2, 3, 4, 5, 6];
const ORDINAL = { 1: "1st", 2: "2nd", 3: "3rd", 4: "4th", 5: "5th", 6: "6th" };
const TOTAL_COLS = 7 + POSITION_COLS.length + 2; // meta cols (incl. Date) + positions + … + Last

/** ISO date → "Sat 6 Jun" (needed once the table spans multiple days). */
const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
};

const badgeCls = {
  early: "bg-emerald-100 text-emerald-700",
  raceDay: "bg-blue-100 text-blue-700",
  late: "bg-purple-100 text-purple-700",
};

/** Inline label for a runner: numbered name (tip pill if tipped) + SP. */
function RunnerLabel({ runner }) {
  const { number, name } = parseHorseFormattedName(runner);
  const price =
    runner.sp != null ? (
      <span className="text-slate-400"> ${runner.sp}</span>
    ) : null;

  if (runner.tip) {
    const pillCls = runner.tip.hit
      ? "bg-emerald-500 text-white rounded px-1.5 py-0.5 font-semibold"
      : "bg-slate-200 text-slate-600 rounded px-1.5 py-0.5";
    return (
      <span className="inline-flex items-center gap-1">
        <span className={pillCls}>
          {number}. {name}
        </span>
        <span
          className={`text-[9px] rounded px-1 ${badgeCls[runner.tip.edition] || "bg-slate-100 text-slate-600"}`}
        >
          {tipBadge(runner.tip)}
        </span>
        {price}
      </span>
    );
  }

  return (
    <span>
      {number}. {name}
      {price}
    </span>
  );
}

function RunnerCell({ runner }) {
  if (!runner) return <td className="px-3 py-2 text-slate-300">—</td>;
  const tint = runner.inRange ? "px-3 py-2 bg-emerald-50" : "px-3 py-2";
  return (
    <td className={tint}>
      <RunnerLabel runner={runner} />
    </td>
  );
}

export default function RacePerformanceTable({ rows }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // A second horizontal scrollbar pinned to the top of the viewport, kept in
  // sync with the table's own scroll — so it's reachable no matter how far down
  // a long (monthly) list you've scrolled.
  const topRef = useRef(null);
  const bodyRef = useRef(null);
  const tableRef = useRef(null);
  const [tableWidth, setTableWidth] = useState(0);
  useEffect(() => {
    const el = tableRef.current;
    if (!el) return;
    const measure = () => setTableWidth(el.scrollWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [rows]);
  const onTopScroll = () => {
    if (bodyRef.current) bodyRef.current.scrollLeft = topRef.current.scrollLeft;
  };
  const onBodyScroll = () => {
    if (topRef.current) topRef.current.scrollLeft = bodyRef.current.scrollLeft;
  };

  return (
    <div className="mb-6">
      {/* Synced scrollbar pinned to the top of the viewport. */}
      <div
        ref={topRef}
        onScroll={onTopScroll}
        className="sticky top-0 z-20 overflow-x-auto overflow-y-hidden bg-slate-100"
      >
        <div style={{ width: tableWidth, height: 1 }} />
      </div>
      <div
        ref={bodyRef}
        onScroll={onBodyScroll}
        className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto"
      >
        <table
          ref={tableRef}
          className="text-xs whitespace-nowrap min-w-[1100px]"
        >
          <thead className="bg-slate-50 text-[11px] text-slate-500 uppercase">
            <tr>
              <th className="px-3 py-2 text-left sticky left-0 bg-slate-50 z-10">
                Time
              </th>
              <th className="px-2 py-2 text-left">Date</th>
              <th className="px-2 py-2">R#</th>
              <th className="px-2 py-2">Track</th>
              <th className="px-2 py-2">Field</th>
              <th className="px-2 py-2">Paid</th>
              <th className="px-2 py-2">Result</th>
              {POSITION_COLS.map((p) => (
                <th key={p} className="px-3 py-2 text-left">
                  {ORDINAL[p]}
                </th>
              ))}
              <th className="px-3 py-2 text-left">…</th>
              <th className="px-3 py-2 text-left">Last</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const byPos = new Map(row.runners.map((r) => [r.position, r]));
              const hasGap = row.field > 7;
              const lastRunner = row.field > 6 ? byPos.get(row.field) : null;
              const isOpen = expanded.has(row.raceId);
              const middle = row.runners.filter(
                (r) => r.position >= 7 && r.position <= row.field - 1,
              );
              return (
                <Fragment key={row.raceId}>
                  <tr
                    className={
                      row.cashed
                        ? "border-l-4 border-emerald-500 border-b border-slate-100"
                        : "border-l-4 border-transparent border-b border-slate-100"
                    }
                  >
                    <td className="px-3 py-2 sticky left-0 bg-white font-medium z-10">
                      {row.startTime}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-slate-600">
                      {fmtDate(row.date)}
                    </td>
                    <td className="px-2 py-2 text-center">{row.raceNumber}</td>
                    <td className="px-2 py-2">{row.track}</td>
                    <td className="px-2 py-2 text-center">{row.field}</td>
                    <td className="px-2 py-2 text-center font-semibold">
                      {row.paid}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {row.cashed ? (
                        <span className="text-emerald-600 font-semibold">
                          ✓ cashed
                        </span>
                      ) : (
                        <span className="text-slate-400 font-semibold">
                          ✕ missed
                        </span>
                      )}
                    </td>
                    {POSITION_COLS.map((p) => (
                      <RunnerCell key={p} runner={byPos.get(p) || null} />
                    ))}
                    <td className="px-3 py-2 text-center">
                      {hasGap ? (
                        <button
                          type="button"
                          onClick={() => toggle(row.raceId)}
                          className="text-slate-500 hover:text-emerald-700 font-semibold"
                          title={
                            isOpen
                              ? "Hide middle placings"
                              : "Show all placings"
                          }
                          aria-expanded={isOpen}
                        >
                          {isOpen ? "‹ hide" : `+${row.field - 7} …`}
                        </button>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <RunnerCell runner={lastRunner} />
                  </tr>
                  {isOpen && hasGap && (
                    <tr className="bg-slate-50/70 border-b border-slate-100">
                      <td
                        colSpan={TOTAL_COLS}
                        className="px-4 py-2 whitespace-normal"
                      >
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                          <span className="text-[10px] uppercase tracking-wide text-slate-400">
                            7th–{row.field - 1}th
                          </span>
                          {middle.map((r) => (
                            <RunnerLabel key={r.selectionId} runner={r} />
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={TOTAL_COLS}
                  className="px-3 py-8 text-center text-slate-400"
                >
                  No races match these filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
